/** Checks for the admin New Upload dialog's order fields. */

/**
 * Validate an order number input string.
 * Returns null if valid, or an error message string if invalid.
 * Valid: non-empty after trim, alphanumeric + hyphens only, max 64 chars.
 */
export function validateOrderNumber(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return 'Order number is required.';
  }
  if (trimmed.length > 64) {
    return 'Order number must be 64 characters or fewer.';
  }
  if (!/^[a-zA-Z0-9-]+$/.test(trimmed)) {
    return 'Order number can only contain letters, numbers, and hyphens.';
  }
  return null;
}

/**
 * Validate an order number for the backfill form, where a single leading `#` is
 * allowed. Shopify's order name carries the store prefix, so a synced record
 * holds "#CDL1014" — a backfilled one is typed by hand and has to be able to
 * match. Mirrors `validate_backfill_order_number` in the admin handler.
 */
export function validateBackfillOrderNumber(input: string): string | null {
  const trimmed = input.trim();
  return validateOrderNumber(trimmed.startsWith('#') ? trimmed.slice(1) : trimmed);
}

/**
 * Validate a Shopify numeric identifier. Must be bare digits: these become the
 * order record's keys, and a `gid://` URI would file it where nothing else looks.
 */
export function validateShopifyId(input: string, label: string): string | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return `${label} is required.`;
  }
  if (!/^[0-9]+$/.test(trimmed)) {
    return `${label} must be the numeric ID only (no gid:// prefix).`;
  }
  return null;
}

/**
 * Convert the `yyyy-mm-dd` value of a date input into the RFC 3339 timestamp the
 * API expects. The time is not asked for: `shopify_order_date` drives display
 * and sort order only, so the date is what matters, and midnight UTC keeps the
 * order on the day it was actually placed.
 */
export function orderDateToRfc3339(value: string): string {
  return `${value}T00:00:00Z`;
}
